import { Delete, Edit } from '@mui/icons-material';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { IconButton, LinearProgress, Pagination, Paper, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow, Typography } from '@mui/material';

import { useDebounce } from '../../shared/hooks';
import { ExportButtons, Toolbar } from '../../shared/components';
import { IClientCompany } from 'shared/protocolos';
import { ClientCompanyService } from 'shared/services';
import { Environment } from '../../shared/environments';
import { LayoutBaseDePagina } from '../../shared/layouts';

export const ClientCompany = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { debounce } = useDebounce();

    const [rows, setRows] = useState<IClientCompany[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [totalCount, setTotalCount] = useState(0);
    const userId = localStorage.getItem('LOGGED_USER_ID');

    const busca = useMemo(() => searchParams.get('busca') || '', [searchParams]);
    const pagina = useMemo(() => Number(searchParams.get('pagina') || '1'), [searchParams]);

    const handleDelete = async (id: string) => {
        if (window.confirm('Deseja realmente apagar?')) {
            const result = await ClientCompanyService.deleteById(id, userId as string);
            if (result instanceof Error) {
                alert(result.message);
            } else {
                handleTableList();
            }
        }
    };

    const handleTableList = async () => {
        setIsLoading(true);
        await ClientCompanyService.getAll(busca, pagina, Environment.LIMIT).then(result => {
            setIsLoading(false);
            if (result instanceof Error) {
                alert(result.message);
            } else {
                setRows(result.data);
                setTotalCount(result.totalCount);
            }
        });
    };

    useEffect(() => {
        debounce(() => {
            handleTableList();
        });
    }, [busca, pagina]);

    return (
        <LayoutBaseDePagina
            barraDeFerramentas={
                <Toolbar
                    textoBotaoNovo="Nova"
                    mostrarInputBusca
                    textoDaBusca={busca}
                    acoesExtras={<ExportButtons title="Empresas Parceiras" fileName="empresas-parceiras" rows={rows} totalCount={totalCount} pageSize={Environment.LIMIT}
                        loadPage={async exportPage => { const result = await ClientCompanyService.getAll(busca, exportPage, Environment.LIMIT); return result instanceof Error ? result : result.data; }} disabled={isLoading} columns={[
                        { label: 'Nome', value: row => row.name }, { label: 'CNPJ', value: row => row.cnpj },
                        { label: 'Nome do contrato', value: row => row.contactName }, { label: 'Email', value: row => row.contactEmail },
                        { label: 'Telefone', value: row => row.contactPhone }, { label: 'Nome do responsável', value: row => row.user?.name },
                        { label: 'CPF do responsável', value: row => row.user?.cpf }, { label: 'Email do responsável', value: row => row.user?.email },
                        { label: 'Telefone do responsável', value: row => row.user?.telefone },
                    ]} />}
                    aoMudarTextoDeBusca={texto => setSearchParams({ busca: texto, pagina: '1' }, { replace: true })}
                    aoClicarEmNovo={() => navigate('/clientCompany/detalhe/nova')}
                />
            }
        >
            <TableContainer component={Paper} variant="outlined" sx={{ m: 1, width: 'auto' }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell width={100}>Ações</TableCell>
                            <TableCell>Nome</TableCell>
                            <TableCell>CNPJ</TableCell>
                            <TableCell>Nome do contrato</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Telefone</TableCell>
                            <TableCell>Nome do responsável</TableCell>
                            <TableCell>CPF do responsável</TableCell>
                            <TableCell>Email do responsável</TableCell>
                            <TableCell>Telefone do responsável</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map(row => (
                            <TableRow key={row.id}>
                                <TableCell>
                                    <IconButton onClick={() => navigate(`/clientCompany/detalhe/${row.id}`)} size="small">
                                        <Edit />
                                    </IconButton>
                                    <IconButton onClick={() => handleDelete(row.id)} size="small">
                                        <Delete />
                                    </IconButton>
                                </TableCell>
                                <TableCell>{row.name}</TableCell>
                                <TableCell>{row.cnpj}</TableCell>
                                <TableCell>{row.contactName}</TableCell>
                                <TableCell>{row.contactEmail}</TableCell>
                                <TableCell>{row.contactPhone}</TableCell>
                                <TableCell>{row.user?.name}</TableCell>
                                <TableCell>{row.user?.cpf}</TableCell>
                                <TableCell>{row.user?.email}</TableCell>
                                <TableCell>{row.user?.telefone}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>

                    {totalCount === 0 && !isLoading && (
                        <caption>{Environment.LISTAGEM_VAZIA}</caption>
                    )}

                    <TableFooter>
                        {isLoading && (
                            <TableRow>
                                <TableCell colSpan={4}>
                                    <LinearProgress variant="indeterminate" />
                                </TableCell>
                            </TableRow>
                        )}
                        {(totalCount > 0 && totalCount > Environment.LIMIT) && (
                            <TableRow>
                                <TableCell colSpan={4}>
                                    <Pagination
                                        page={pagina}
                                        count={Math.ceil(totalCount / Environment.LIMIT)}
                                        onChange={(_, newPage) => setSearchParams({ busca, pagina: newPage.toString() }, { replace: true })}
                                    />
                                </TableCell>
                            </TableRow>
                        )}
                    </TableFooter>
                </Table>
            </TableContainer>
        </LayoutBaseDePagina>
    );
};
